import type { ReactNode } from 'react';

import { cx } from '../lib/cx.ts';

/*
 * There is no `suggested` state, deliberately. HiLLO chips used to render
 * proposed outcomes with a dashed, transparent, `text-faint` treatment — which
 * was *lower* contrast than an ordinary unselected chip, so a suggestion read
 * as "unavailable" rather than "recommended". Since the suggestion only tinted
 * chips (it never pre-selected, reordered or filtered), the two-tier appearance
 * cost more in confusion than the hint was worth. Removed 2026-08-19; see
 * CURRICULUM.md. `suggestHillos` still exists in the shared package.
 */
type ChipProps = {
  children: ReactNode;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** Only for a chip used inside a multi-select group. */
  pressed?: boolean;
  className?: string;
};

export function Chip({
  children,
  selected = false,
  disabled = false,
  onClick,
  pressed,
  className,
}: ChipProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={pressed}
      onClick={onClick}
      className={cx(
        'inline-flex min-h-touch items-center rounded-full border px-4',
        'text-sm font-medium whitespace-nowrap',
        'transition-colors duration-(--ui-duration-fast)',
        'disabled:pointer-events-none disabled:opacity-50',
        selected
          ? 'border-accent bg-accent-quiet text-accent'
          : 'border-line bg-surface-raised text-muted hover:border-line-strong hover:text-text',
        className,
      )}
    >
      {children}
    </button>
  );
}
